import React from 'react'
import { createHashRouter, Navigate, useParams } from 'react-router-dom'

import lazy from '@helpers/lazy'
import routes from '@msteams/Routes/routes'
import ContactListLayout from '@pages/Accounting/Contacts/components/ContactListLayout'
import { ProtectedRoute } from '@src/Routes'
import TeamHome from '@src/pages/Team/components/TeamHome'
import { Perm, TEAM_SETTINGS_PERMS } from '@type/permissions'
import ContactLayout from '@pages/Accounting/Contacts/components/ContactLayout'
import AppLayout from '@msteams/components/AppLayout'
import OnboardingLayout from '@msteams/components/OnboardingLayout'
import TeamLayout from '@pages/Team/components/Layout'

import CostCenterList from '@src/pages/Accounting/CostCenter/List'
import CostCenterForm from '@src/pages/Accounting/CostCenter/Form'
import CostCenterDetails from '@src/pages/Accounting/CostCenter/Details'
import CostCenterDetailsBudget from '@src/pages/Accounting/CostCenter/Details/components/BudgetTab'
import Paywall from '@pages/Paywall'

const ContractsList = lazy(() => import('@src/pages/Accounting/Contracts/List'), 'ContractsList')
const ContractForm = lazy(() => import('@src/pages/Accounting/Contacts/Contracts/ContractForm'), 'ContractForm')
const QuotesList = lazy(() => import('@src/pages/Accounting/Quotes/List'), 'QuotesList')
const QuoteDetails = lazy(() => import('@src/pages/Accounting/Quotes/Details'), 'QuoteDetails')
const VendorsList = lazy(() => import('@src/pages/Accounting/Contacts/ContactsList'), 'VendorsList')
const VendorForm = lazy(() => import('@src/pages/Accounting/Contacts/ContactForm'), 'VendorForm')
const InvoicesList = lazy(() => import('@src/pages/Accounting/Invoices/List'), 'InvoicesList')
const InvoiceDetails = lazy(() => import('@src/pages/Accounting/Invoices/Details'), 'InvoiceDetails')
const PurchaseOrdersList = lazy(() => import('@src/pages/Accounting/PurchaseOrders/List'), 'PurchaseOrdersList')
const PurchaseOrderDetails = lazy(() => import('@src/pages/Accounting/PurchaseOrders/Details'), 'PurchaseOrderDetails')
const VendorContractsList = lazy(
  () => import('@src/pages/Accounting/Contacts/Contracts/ContractsList'),
  'VendorContractsList',
)

const ApprovalRequests = lazy(() => import('@msteams/pages/ApprovalRequests'), 'ApprovalRequests')
const ApprovalRequestDetails = lazy(() => import('@msteams/pages/ApprovalRequestDetails'), 'ApprovalRequestDetails')

const ApprovalRequestsForm = lazy(
  () => import('@src/pages/ApprovalRequests/RequestForm/CustomForm'),
  'ApprovalRequestsForm',
)

const PipelineBuilder = lazy(() => import('@pages/Pipelines/Builder'), 'PipelineBuilder')
const PipelinesList = lazy(() => import('@pages/Pipelines/PipelinesList'), 'PipelinesList')
const ProcessDetails = lazy(() => import('@pages/Pipelines/ProcessDetails'), 'ProcessDetails')
const ProcessesList = lazy(() => import('@pages/Pipelines/ProcessesList'), 'ProcessesList')

const TeamDashboard = lazy(() => import('@msteams/pages/TeamDashboard'), 'TeamDashboard')
const Config = lazy(() => import('@msteams/pages/Config'), 'Config')
const NotSynchronized = lazy(() => import('@msteams/pages/NotSynchronized'), 'NotSynchronized')

const RFQsList = lazy(() => import('@src/pages/Accounting/RFQ/List'), 'RfqList')

/** MS Teams contact list route gate — mirrors the web app: the
 * sidebar exposes the list via Perm.VENDOR_VIEW / Perm.CUSTOMER_VIEW,
 * so the route can't gate on ACCOUNTING_MANAGE without redirecting
 * view-only users. Resolve the matching view perm from the
 * ``contactParty`` URL param.
 */
export const MsTeamsContactsListGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { contactParty } = useParams<{ contactParty?: string }>()
  // Only 'customer' / 'vendor' (or legacy undefined → vendor) are valid; a
  // malformed party must not silently authorize as a vendor route.
  if (contactParty && contactParty !== 'customer' && contactParty !== 'vendor') {
    return <Navigate to={routes.public.notFound} replace />
  }
  const perm = contactParty === 'customer' ? Perm.CUSTOMER_VIEW : Perm.VENDOR_VIEW
  return <ProtectedRoute requirePerm={perm}>{children}</ProtectedRoute>
}
const RFQDetails = lazy(() => import('@src/pages/Accounting/RFQ/Details'), 'RfqDetails')
const RFQProposalDetails = lazy(() => import('@src/pages/Accounting/RFQ/ProposalDetails'), 'RfqProposalDetails')

const Auth = lazy(() => import('@msteams/pages/Auth'), 'Auth')
const AuthLayout = lazy(() => import('@msteams/components/AuthLayout'), 'AuthLayout')

const Tasks = lazy(() => import('@pages/Tasks/List'), 'Tasks')

const NotFound = lazy(() => import('@pages/NotFound'), 'NotFound')
const Onboarding = lazy(() => import('@pages/Onboarding'), 'Onboarding')
const Assistant = lazy(() => import('@pages/Assistant'), 'Assistant')

const SettingsPage = lazy(() => import('@src/pages/Settings'), 'SettingsPage')
const TeamSettingsPage = lazy(() => import('@src/pages/TeamSettings'), 'TeamSettingsPage')

const ReportsList = lazy(() => import('@pages/Reports/List'), 'ReportsList')
const AIReports = lazy(() => import('@pages//Reports/List/AIReports'), 'AIReports')
const ManualReports = lazy(() => import('@pages/Reports/List/ManualReports'), 'ManualReports')
const ReportForm = lazy(() => import('@pages/Reports/ReportForm'), 'ReportForm')

const Mailbox = lazy(() => import('@pages/Mailbox'), 'Mailbox')

const OrganizationalStructureList = lazy(() => import('@src/pages/Team/Members/List'), 'OrganizationalStructureList')
const OrganizationalStructureMember = lazy(
  () => import('@src/pages/Team/Members/Member'),
  'OrganizationalStructureMember',
)
const TeamDepartments = lazy(() => import('@src/pages/Team/Departments'), 'TeamDepartments')
const TeamLocations = lazy(() => import('@src/pages/Team/Locations'), 'TeamLocations')
const TeamRoles = lazy(() => import('@src/pages/Team/Roles'), 'TeamRoles')
const OrganisationsList = lazy(
  () => import('@src/pages/Accounting/Organizations/OrganizationsList'),
  'OrganisationsList',
)
const OrganizationForm = lazy(() => import('@src/pages/Accounting/Organizations/OrganizationForm'), 'OrganizationForm')

const FinanceSettingsLayout = lazy(
  () => import('@src/pages/Accounting/FinanceSettings/components/Layout'),
  'FinanceSettingsLayout',
)
const AccountsList = lazy(() => import('@src/pages/Accounting/Accounts/AccountsList'), 'AccountsList')
const CategoriesList = lazy(() => import('@src/pages/Accounting/Accounts/CategoriesList'), 'CategoriesList')
const CompanyDetails = lazy(() => import('@src/pages/Accounting/FinanceSettings/CompanyDetails'), 'CompanyDetails')
const TaxList = lazy(() => import('@src/pages/Accounting/Taxes/TaxList'), 'TaxList')
const ProductsList = lazy(() => import('@src/pages/Accounting/Products/List'), 'ProductsList')
const EntitiesList = lazy(() => import('@src/pages/Accounting/FinanceSettings/Entities'), 'EntitiesList')

const commonRoutes = [
  {
    path: routes.public.auth,
    element: <Auth />,
  },
  {
    path: routes.public.notFound,
    element: <NotFound />,
  },
]

// Mirror the web app's PipelineByIdGate: ``/workflow/new`` is a create
// (WORKFLOW_ADD), an existing id is an edit (WORKFLOW_CHANGE). Hardcoding
// CHANGE locked WORKFLOW_ADD-only roles out of creating workflows in Teams.
export const MsTeamsPipelineByIdGate: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const isCreate = id === 'new'
  return (
    <ProtectedRoute requirePerm={isCreate ? Perm.WORKFLOW_ADD : Perm.WORKFLOW_CHANGE}>
      <PipelineBuilder />
    </ProtectedRoute>
  )
}

export const personalRouter = createHashRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        element: <OnboardingLayout />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.tab,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.dashboard,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.approvalRequests,
                element: <ApprovalRequests />,
              },
              {
                path: routes.private.approvalRequestsById,
                element: <ApprovalRequestDetails />,
              },
              {
                path: routes.private.approvalRequestsByIdAction,
                element: <ApprovalRequestDetails />,
              },
              {
                path: routes.private.approvalRequest,
                element: <ApprovalRequestsForm />,
              },
              {
                path: routes.private.approvalRequestEditById,
                element: <ApprovalRequestsForm />,
              },
              {
                path: routes.private.notSynchronized,
                element: <NotSynchronized />,
              },
              {
                path: routes.private.tasks,
                element: <Tasks />,
              },
              {
                path: routes.private.taskBoards,
                element: <Tasks />,
              },
              // AI Assistant — mirrors the web router pattern at
              // src/Routes/index.tsx (parent route + ':threadId' child
              // sub-route so /assistant/:threadId resolves to the same
              // <Assistant /> component, with React Router driving the
              // thread switch via URL params).
              {
                path: routes.private.assistant,
                element: <Assistant />,
                children: [{ path: ':threadId', element: null }],
              },

              {
                path: routes.private.purchaseOrders,
                element: <PurchaseOrdersList />,
              },
              {
                path: routes.private.purchaseOrdersById,
                element: <PurchaseOrderDetails />,
              },
              {
                path: routes.private.bills,
                element: <InvoicesList />,
              },
              {
                path: routes.private.billById,
                element: <InvoiceDetails />,
              },

              {
                path: routes.private.invoices,
                element: <InvoicesList />,
              },
              {
                path: routes.private.invoiceById,
                element: <InvoiceDetails />,
              },

              {
                path: routes.private.quotes,
                element: <QuotesList />,
              },

              {
                path: routes.private.quoteById,
                element: <QuoteDetails />,
              },

              {
                element: <ContactListLayout />,
                children: [
                  {
                    path: routes.private.contacts,
                    element: (
                      <MsTeamsContactsListGate>
                        <VendorsList />
                      </MsTeamsContactsListGate>
                    ),
                  },
                  {
                    path: routes.private.contactsList,
                    element: (
                      <MsTeamsContactsListGate>
                        <VendorsList />
                      </MsTeamsContactsListGate>
                    ),
                  },
                ],
              },
              {
                element: <ContactLayout />,
                children: [
                  {
                    path: routes.private.contact,
                    element: (
                      <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE}>
                        <VendorForm />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.contactContracts,
                    element: (
                      <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
                        <VendorContractsList />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.contactContract,
                element: (
                  <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
                    <ContractForm />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.processes,
                element: (
                  <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
                    <ProcessesList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcesses,
                element: (
                  <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
                    <ProcessesList />
                  </ProtectedRoute>
                ),
              },
              { path: routes.private.pipelines, element: <PipelinesList /> },
              {
                path: routes.private.pipelineProcessesById,
                element: (
                  <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
                    <ProcessDetails />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcesses,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.pipelineApprovalRequest,
                element: <ApprovalRequestsForm />,
              },
              {
                path: routes.private.pipelineProcessApprovalRequest,
                element: <ApprovalRequestsForm />,
              },
              {
                path: routes.private.pipelineById,
                element: <MsTeamsPipelineByIdGate />,
              },
              {
                path: routes.private.rfqs,
                element: <RFQsList />,
              },
              {
                path: routes.private.rfqProposalsById,
                element: <RFQProposalDetails />,
              },
              {
                path: routes.private.rfqById,
                element: <RFQDetails />,
              },

              {
                path: routes.private.settings,
                element: <SettingsPage />,
                children: [
                  {
                    index: true,
                    path: routes.private.settingsRouted,
                    element: <SettingsPage />,
                  },
                ],
              },
              // Team (Workspace) Settings — mirror of the web router so the
              // Teams embed reaches the same tabs (Team, Tags, Integrations,
              // Notifications, Audit Logs, API Dashboard). Same shared page +
              // TEAM_SETTINGS_PERMS gate; the page filters tabs per-perm.
              {
                path: routes.private.teamSettings,
                element: (
                  <ProtectedRoute requireAnyPerm={TEAM_SETTINGS_PERMS}>
                    <TeamSettingsPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.teamSettingsRouted,
                element: (
                  <ProtectedRoute requireAnyPerm={TEAM_SETTINGS_PERMS}>
                    <TeamSettingsPage />
                  </ProtectedRoute>
                ),
              },

              {
                element: <ReportsList />,
                children: [
                  {
                    path: routes.private.reports,
                    element: <ManualReports />,
                  },
                  {
                    path: routes.private.aiReports,
                    element: (
                      <ProtectedRoute onlyWithReportsAvailable requirePerm={Perm.REPORT_VIEW}>
                        <AIReports />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.createReport,
                element: <ReportForm />,
              },
              {
                path: routes.private.reportById,
                element: <ReportForm />,
              },

              {
                path: routes.private.mailbox,
                element: (
                  <ProtectedRoute requirePerm={Perm.MAILBOX_VIEW}>
                    <Mailbox />
                  </ProtectedRoute>
                ),
              },
              {
                element: <TeamLayout />,
                children: [
                  {
                    path: routes.private.organizationalStructure,
                    element: (
                      <ProtectedRoute requireAnyPerm={[Perm.TEAM_VIEW, Perm.TEAM_MANAGE_ORG_STRUCTURE, Perm.ROLE_VIEW]}>
                        <TeamHome roster={<OrganizationalStructureList />} />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamDepartments,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                        <TeamDepartments />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamDepartmentById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                        <TeamDepartments />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamLocations,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                        <TeamLocations />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamLocationById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                        <TeamLocations />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamRoles,
                    element: (
                      <ProtectedRoute requirePerm={Perm.ROLE_VIEW}>
                        <TeamRoles />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.organizationalStructureById,
                element: (
                  <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                    <OrganizationalStructureMember />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.contracts,
                element: (
                  <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
                    <ContractsList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.organizations,
                element: (
                  <ProtectedRoute onlyWithOrgsAvailable requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
                    <OrganisationsList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.organization,
                element: (
                  <ProtectedRoute onlyWithOrgsAvailable requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
                    <OrganizationForm />
                  </ProtectedRoute>
                ),
              },
              {
                element: <FinanceSettingsLayout />,
                children: [
                  {
                    path: routes.private.accounts,
                    element: (
                      <ProtectedRoute requirePerm={Perm.CHART_OF_ACCOUNTS_VIEW}>
                        <AccountsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.CHART_OF_ACCOUNTS_VIEW}>
                        <AccountsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.taxes,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TAX_VIEW}>
                        <TaxList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.taxById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.TAX_VIEW}>
                        <TaxList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.products,
                    element: (
                      <ProtectedRoute requirePerm={Perm.PRODUCT_VIEW}>
                        <ProductsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.productById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.PRODUCT_VIEW}>
                        <ProductsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountCategories,
                    element: (
                      <ProtectedRoute requirePerm={Perm.CATEGORY_VIEW}>
                        <CategoriesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountCategoryById,
                    element: (
                      <ProtectedRoute requirePerm={Perm.CATEGORY_VIEW}>
                        <CategoriesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    // Entities tab (SoftLedger Locations) — the shared
                    // FinanceSettings header shows it in Teams too, but the
                    // route was only ever registered in the web router
                    // (ADN-436 parity miss). Same gate as web.
                    path: routes.private.entities,
                    element: (
                      <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
                        <EntitiesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.companyDetails,
                    element: (
                      <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
                        <CompanyDetails />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.financeSettings,
                    element: <Navigate to={routes.private.accounts} />,
                  },
                ],
              },
              {
                path: routes.private.costCenter,
                element: (
                  <ProtectedRoute requirePerm={Perm.COST_CENTER_VIEW}>
                    <CostCenterList />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.costCenterById,
                element: (
                  <ProtectedRoute requirePerm={Perm.COST_CENTER_VIEW}>
                    <CostCenterDetails />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="budget" replace />,
                  },

                  {
                    path: routes.private.costCenterBudget,
                    element: <CostCenterDetailsBudget />,
                  },
                ],
              },
              {
                path: routes.private.costCenterEditById,
                element: (
                  <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE}>
                    <CostCenterForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.paywall,
                element: <Paywall />,
              },
            ],
          },
        ],
      },
      {
        path: routes.private.onboarding,
        element: <Onboarding />,
      },
    ],
  },

  ...commonRoutes,
])

export const teamRouter = createHashRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        element: <OnboardingLayout />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.tab,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.dashboard,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.approvalRequests,
                element: <TeamDashboard />,
              },
              {
                path: routes.private.assistant,
                element: <Assistant />,
                children: [{ path: ':threadId', element: null }],
              },
              {
                path: routes.private.notSynchronized,
                element: <NotSynchronized />,
              },
            ],
          },
        ],
      },
      {
        path: routes.private.onboarding,
        element: <Onboarding />,
      },
    ],
  },
  {
    path: routes.private.config,
    element: <Config />,
  },
  ...commonRoutes,
])
